import Logger from "@/lib/utils/logger";
/**
 * Cookie utilities for affinity code management
 * Supports both server-side (Next.js) and client-side usage
 */

const AFFINITY_COOKIE_NAME = "affinity_code";
const AFFINITY_COOKIE_MAX_AGE = 60 * 60; // 1 hour in seconds

/**
 * Get affinity code from cookies (server-side)
 * @returns The affinity code or null if not found
 */
export async function getAffinityCodeServer(): Promise<string | null> {
  if (typeof window !== "undefined") {
    throw new Error("getAffinityCodeServer can only be used server-side");
  }

  try {
    const { cookies } = await import("next/headers");
    const cookieStore = await cookies();
    return cookieStore.get(AFFINITY_COOKIE_NAME)?.value || null;
  } catch (error) {
    Logger.error("Error reading affinity cookie:", error);
    return null;
  }
}

/**
 * Get affinity code from cookies (client-side)
 * @returns The affinity code or null if not found
 */
export function getAffinityCodeClient(): string | null {
  if (typeof window === "undefined") {
    throw new Error("getAffinityCodeClient can only be used client-side");
  }

  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === AFFINITY_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Get affinity code from cookies (universal - detects server vs client)
 * @returns The affinity code or null if not found
 */
export async function getAffinityCode(): Promise<string | null> {
  if (typeof window === "undefined") {
    return getAffinityCodeServer();
  }
  return getAffinityCodeClient();
}

/**
 * Set affinity code cookie (client-side only)
 * @param code - The affinity code to set
 */
export function setAffinityCode(code: string): void {
  if (typeof window === "undefined") {
    throw new Error("setAffinityCode can only be used client-side");
  }

  const expires = new Date();
  expires.setTime(expires.getTime() + AFFINITY_COOKIE_MAX_AGE * 1000);

  // Build cookie string with proper formatting
  const isSecure = window.location.protocol === "https:";
  const cookieParts = [
    `${AFFINITY_COOKIE_NAME}=${encodeURIComponent(code)}`,
    "path=/",
    `expires=${expires.toUTCString()}`,
    "SameSite=Lax",
  ];
  
  if (isSecure) {
    cookieParts.push("Secure");
  }

  const cookieString = cookieParts.join("; ");
  document.cookie = cookieString;
  
  // Verify cookie was set (for debugging)
  const verifyCookie = document.cookie
    .split(";")
    .find((c) => c.trim().startsWith(`${AFFINITY_COOKIE_NAME}=`));
  
  if (!verifyCookie) {
    Logger.warn("Failed to set affinity cookie", { code, cookieString });
  }
}

/**
 * Clear affinity code cookie (client-side only)
 */
export function clearAffinityCode(): void {
  if (typeof window === "undefined") {
    throw new Error("clearAffinityCode can only be used client-side");
  }

  document.cookie = `${AFFINITY_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/**
 * Set affinity code cookie (server-side)
 * Used in server components and API routes
 * @param code - The affinity code to set
 * @returns Cookie string to set in response headers
 */
export function setAffinityCodeServerCookie(code: string): string {
  const expires = new Date();
  expires.setTime(expires.getTime() + AFFINITY_COOKIE_MAX_AGE * 1000);

  return `${AFFINITY_COOKIE_NAME}=${encodeURIComponent(code)}; Path=/; Expires=${expires.toUTCString()}; SameSite=Lax; HttpOnly=false`;
}
